import { FileTypeName } from 'api/types/entity';
import {
  FormContainer,
  FormContainerProps,
  VerificationCodeFormContentDialog,
} from 'components';
import { FormSubmit } from 'libs/form';
import { useTranslation } from 'libs/i18n';
import { Box, LinkLabel, Typography } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';

import {
  DialogDocumentsStyled,
  DocumentsStyled,
} from './SigningDocumentsWrapper.styles';

export interface SigningDocumentsWrapperProps<Values extends object>
  extends FormContainerProps<Values> {
  documents: FileTypeName[];
  codeFieldName: Extract<keyof Values, string>;
  onClickDocument: (type: FileTypeName) => void;
}

export const SigningDocumentsWrapper = <Values extends object>({
  documents,
  codeFieldName,
  submitLabel,
  onSubmit,
  onClickDocument,
  ...formProps
}: SigningDocumentsWrapperProps<Values>) => {
  const { t } = useTranslation();

  const {
    isOpen: isOpenVerificationDialog,
    onClose: onCloseVerificationDialog,
    open: openVerificationDialog,
  } = useDialog();

  const howManyDocuments: 'one' | 'many' =
    documents.length > 1 ? 'many' : 'one';

  const handleSubmit: FormSubmit<Values> = (values, helpers) => {
    if (isOpenVerificationDialog && values[codeFieldName]) {
      onSubmit(values, helpers);
    } else {
      openVerificationDialog();
    }
  };

  return (
    <FormContainer
      {...formProps}
      submitLabel={
        submitLabel ??
        t(`onboarding.signingDocumentsWrapper.submitLabel.${howManyDocuments}`)
      }
      onSubmit={handleSubmit}
    >
      <DocumentsStyled>
        {documents.map((type) => (
          <Box key={type} className="SigningDocumentsWrapper-documentBox">
            <LinkLabel
              as="button"
              label={t(`onboarding.fileTypeName.${type}`)}
              mode="text"
              typographyVariant="bodyBold"
              typographyVariantMobile="captionBold"
              onClick={() => onClickDocument(type)}
            />
          </Box>
        ))}
      </DocumentsStyled>

      <VerificationCodeFormContentDialog<Values>
        codeFieldName={codeFieldName}
        isOpen={isOpenVerificationDialog}
        title={t('onboarding.signingDocumentsWrapper.verificationDialog.title')}
        onClose={onCloseVerificationDialog}
      >
        <DialogDocumentsStyled spacing={8} spacingMobile={4}>
          <Typography variant="bodyBold" variantMobile="captionBold">
            {t(
              `onboarding.signingDocumentsWrapper.verificationDialog.docsTitle.${howManyDocuments}`
            )}
          </Typography>
          <ul>
            {documents.map((type) => (
              <li key={type}>
                <Typography variantMobile="caption">
                  {t(`onboarding.fileTypeName.${type}`)}
                </Typography>
              </li>
            ))}
          </ul>
        </DialogDocumentsStyled>
      </VerificationCodeFormContentDialog>
    </FormContainer>
  );
};
